import { useSyncExternalStore } from 'react';
import { create } from 'zustand';

import { AssistantFeedbackRating, AssistantMessage, AssistantModelName } from '@paragon/contract-library';

import { Attachment } from './attachments';
import type { PlanPreview } from './planPreviewRequest';
import type { RecordedStep, Recording } from './recorder';

export interface Plan {
  readonly summary: string;
  readonly steps: readonly string[];
  /** What the Plan changes, drawn on the view while it waits for approval. */
  readonly preview?: PlanPreview | null;
}

export interface PlanDecision {
  readonly approved: boolean;
  readonly feedback?: string;
  /** Approved by the auto-approve setting rather than by the designer. */
  readonly automatic?: boolean;
}

/**
 * What the panel shows. The conversation sent to the model is kept separately, in `messages`, because it holds tool
 * results and attachments the designer doesn't need to read.
 */
export type ChatEntry =
  | {
      readonly type: 'user';
      readonly text: string;
      readonly attachments?: readonly { readonly name: string; readonly kind: Attachment['kind'] }[];
    }
  | { readonly type: 'assistant'; readonly text: string }
  | {
      readonly type: 'tool';
      readonly toolName: string;
      readonly status: 'running' | 'done' | 'failed';
      readonly detail?: string;
    }
  | { readonly type: 'plan'; readonly plan: Plan; readonly decision: PlanDecision | null }
  | {
      readonly type: 'fixes';
      readonly problem: string;
      /** Each fix's `request` is sent as the designer's next message when they pick it. */
      readonly fixes: readonly { readonly label: string; readonly request: string }[];
    }
  | { readonly type: 'notice'; readonly text: string }
  | {
      /** The designer's feedback on one Turn, shown under it, and sent to the team as they give it. */
      readonly type: 'feedback';
      /** Every send of this feedback replaces the last under this guid. */
      readonly guid: string;
      readonly turnGuid: string;
      readonly request: string;
      readonly reply: string;
      readonly rating: AssistantFeedbackRating | null;
      readonly comment: string;
      readonly recording: Recording | null;
      readonly status: 'unsent' | 'sending' | 'sent' | 'failed';
    };

/** A Show me how recording in progress. Only one runs at a time, since it watches the whole app. */
export interface ActiveRecording {
  readonly chatId: string;
  /** The feedback entry it belongs to. */
  readonly entryIndex: number;
  readonly startedAt: string;
  readonly steps: readonly RecordedStep[];
  readonly notes: readonly RecordedStep[];
  readonly stop: () => void;
}

interface PendingPlan {
  readonly plan: Plan;
  readonly resolve: (decision: PlanDecision) => void;
}

interface PendingPoint {
  readonly question: string;
  readonly cancel: () => void;
}

/**
 * One chat window: its own conversation with the model, and its own Plan and Stop.
 */
export interface Chat {
  readonly id: string;
  readonly minimized: boolean;
  readonly model: AssistantModelName;
  readonly conversationGuid: string;
  readonly messages: readonly AssistantMessage[];
  readonly entries: readonly ChatEntry[];
  readonly running: boolean;
  readonly stopRequested: boolean;
  readonly pendingPlan: PendingPlan | null;
  /** A question the Assistant asked the designer to answer by clicking in the view. */
  readonly pendingPoint: PendingPoint | null;
}

export interface AssistantState {
  /** Whether the chat windows are shown. Closing the last window hides it and keeps its conversation. */
  readonly open: boolean;
  readonly chats: readonly Chat[];
  readonly autoApprovePlans: boolean;
  readonly recording: ActiveRecording | null;
}

const AUTO_APPROVE_PLANS_KEY = 'paragon.assistant.autoApprovePlans';

// Remembered per browser, like the panel's other conveniences; storage can be unavailable, so it defaults to off.
function readAutoApprovePlans() {
  try {
    return localStorage.getItem(AUTO_APPROVE_PLANS_KEY) === 'true';
  } catch {
    return false;
  }
}

function createChat(model: AssistantModelName = 'haiku'): Chat {
  return {
    id: crypto.randomUUID(),
    minimized: false,
    model,
    conversationGuid: crypto.randomUUID(),
    messages: [],
    entries: [],
    running: false,
    stopRequested: false,
    pendingPlan: null,
    pendingPoint: null,
  };
}

function createInitialState(): AssistantState {
  return {
    open: false,
    chats: [createChat()],
    autoApprovePlans: readAutoApprovePlans(),
    recording: null,
  };
}

// Conversations last for the browser session: reloading the page starts fresh.
export const assistantStore = create<AssistantState>(createInitialState);

export function useAssistantState<T>(selector: (state: AssistantState) => T): T {
  return useSyncExternalStore(assistantStore.subscribe, () => selector(assistantStore.getState()));
}

export function getChat(chatId: string): Chat | undefined {
  return assistantStore.getState().chats.find((chat) => chat.id === chatId);
}

const EMPTY_CHAT = createChat();

/**
 * Reads one chat for a window. The window goes away with its chat, so the chat is always there while it renders.
 */
export function useChat<T>(chatId: string, selector: (chat: Chat) => T): T {
  return useAssistantState((state) => selector(state.chats.find((chat) => chat.id === chatId) ?? EMPTY_CHAT));
}

// A chat that was closed takes no more changes, though a Turn it started may still be finishing.
function updateChat(chatId: string, update: (chat: Chat) => Partial<Chat>) {
  assistantStore.setState((state) => ({
    chats: state.chats.map((chat) => (chat.id === chatId ? { ...chat, ...update(chat) } : chat)),
  }));
}

// Opening the windows always shows them in full.
export function setAssistantOpen(open: boolean) {
  assistantStore.setState((state) => ({
    open,
    chats: open ? state.chats.map((chat) => ({ ...chat, minimized: false })) : state.chats,
  }));
}

/**
 * Opens another chat window beside the others, using the model of the newest one. Returns its id.
 */
export function openAssistantChat(): string {
  const chat = createChat(assistantStore.getState().chats.at(-1)?.model);
  assistantStore.setState((state) => ({ open: true, chats: [...state.chats, chat] }));
  return chat.id;
}

/**
 * Closes a chat window, stopping its Turn and declining anything it is waiting on. Closing the last window only hides
 * it, so its conversation is there when the Assistant is opened again.
 */
export function closeAssistantChat(chatId: string) {
  const { chats } = assistantStore.getState();
  if (chats.length <= 1) {
    assistantStore.setState({ open: false });
    return;
  }
  declineWaiting(chatId, 'The designer closed this chat.');
  assistantStore.setState({ chats: chats.filter((chat) => chat.id !== chatId) });
}

/**
 * Shrinks a window to its header, so the Assistant can keep working without covering the model.
 */
export function setAssistantMinimized(chatId: string, minimized: boolean) {
  updateChat(chatId, () => ({ minimized }));
}

export function setAssistantModel(chatId: string, model: AssistantModelName) {
  updateChat(chatId, () => ({ model }));
}

/**
 * Turns auto-approving Plans on or off, for every window. Turning it on also approves Plans that are waiting.
 */
export function setAutoApprovePlans(autoApprovePlans: boolean) {
  try {
    localStorage.setItem(AUTO_APPROVE_PLANS_KEY, String(autoApprovePlans));
  } catch {
    // Not remembered, but still on for this session.
  }
  assistantStore.setState({ autoApprovePlans });
  if (autoApprovePlans)
    assistantStore.getState().chats.forEach((chat) => chat.pendingPlan?.resolve({ approved: true, automatic: true }));
}

export function addChatEntry(chatId: string, entry: ChatEntry): number {
  const index = getChat(chatId)?.entries.length ?? 0;
  updateChat(chatId, (chat) => ({ entries: [...chat.entries, entry] }));
  return index;
}

export function updateChatEntry(chatId: string, index: number, entry: ChatEntry) {
  updateChat(chatId, (chat) => ({ entries: chat.entries.map((other, at) => (at === index ? entry : other)) }));
}

export function requestStop(chatId: string) {
  getChat(chatId)?.pendingPoint?.cancel();
  updateChat(chatId, (chat) => (chat.running ? { stopRequested: true } : {}));
}

/**
 * Shows the Assistant's question in its window while it waits for the designer to click in the view. `cancel` ends the
 * wait without a click.
 */
export function askToPoint(chatId: string, question: string, cancel: () => void) {
  updateChat(chatId, () => ({ minimized: false, pendingPoint: { question, cancel } }));
}

export function finishPointing(chatId: string) {
  updateChat(chatId, () => ({ pendingPoint: null }));
}

/**
 * Whether a chat's Turn should stop: the designer asked it to, or closed its window.
 */
export function shouldStopChat(chatId: string) {
  return getChat(chatId)?.stopRequested ?? true;
}

function declineWaiting(chatId: string, feedback: string) {
  getChat(chatId)?.pendingPlan?.resolve({ approved: false, feedback });
  getChat(chatId)?.pendingPoint?.cancel();
}

/**
 * Starts a new conversation in a window. Anything it is waiting on is declined.
 */
export function resetConversation(chatId: string) {
  declineWaiting(chatId, 'The designer started a new conversation.');
  updateChat(chatId, (chat) => ({ ...createChat(chat.model), id: chat.id, minimized: chat.minimized }));
}

/**
 * Shows the Plan in its window and waits for the designer to approve or reject it. With auto-approve on, the Plan is
 * still shown, and approved at once.
 */
export function proposePlan(chatId: string, plan: Plan): Promise<PlanDecision> {
  if (assistantStore.getState().autoApprovePlans) {
    const decision: PlanDecision = { approved: true, automatic: true };
    addChatEntry(chatId, { type: 'plan', plan, decision });
    return Promise.resolve(decision);
  }

  const entryIndex = addChatEntry(chatId, { type: 'plan', plan, decision: null });
  return new Promise((resolve) => {
    assistantStore.setState({ open: true });
    updateChat(chatId, () => ({
      minimized: false,
      pendingPlan: {
        plan,
        resolve: (decision) => {
          updateChatEntry(chatId, entryIndex, { type: 'plan', plan, decision });
          updateChat(chatId, () => ({ pendingPlan: null }));
          resolve(decision);
        },
      },
    }));
  });
}

export function decidePlan(chatId: string, decision: PlanDecision) {
  getChat(chatId)?.pendingPlan?.resolve(decision);
}

export function appendMessages(chatId: string, ...messages: AssistantMessage[]) {
  updateChat(chatId, (chat) => ({ messages: [...chat.messages, ...messages] }));
}

export function setRunning(chatId: string, running: boolean) {
  updateChat(chatId, () => (running ? { running } : { running, stopRequested: false }));
}
